import { OpenAPI, request as __request } from '/src/api';
import { CancelablePromise } from '/src/api/core/CancelablePromise';

export interface BankAccount {
  bank_account: {
    bank_name?: string;
    routing_number?: string;
    last4?: string;
    account_type?: string;
  };
  id: string;
  type: string;
  status?: string;
}

export interface BankingInputProps {
  clientId?: number;
  stripeAccountId?: string;
}

export type StripeGlobalPayoutsAccount = {
  email: string;
  bank_accounts: BankAccount[];
};

export type UpdateEmailRequest = {
  email: string;
};

export type UpdateBankAccountRequest = {
  bank_account_type: 'checking';
  routing_number: string;
  account_number: string;
};

export type CreateRecipientRequest = {
  contact_email: string;
  registered_name: string;
  address?: {
    line1: string;
    line2?: string;
    city: string;
    region_id: number;
    postal_code: string;
  };
};

export type CreateBankAccountRequest = {
  bank_account_type: 'checking' | 'savings';
  routing_number: string;
  account_number: string;
};

export class StripeGlobalPayoutsService {
  /**
   * Get Stripe Global Payouts account info for a client
   * @param clientId The client ID
   * @returns StripeGlobalPayoutsAccount
   * @throws ApiError
   */
  public static getAccount = (clientId: number): CancelablePromise<StripeGlobalPayoutsAccount> =>
    __request(OpenAPI, {
      method: 'GET',
      url: `/clients/${clientId}/stripeGlobalPayouts`,
    });

  /**
   * Update the notification email for a client's Stripe Global Payouts account
   * @param clientId The client ID
   * @param request UpdateEmailRequest
   * @returns updated email info
   * @throws ApiError
   */
  public static updateEmail = (clientId: number, request: UpdateEmailRequest): CancelablePromise<{ email: string }> =>
    __request(OpenAPI, {
      method: 'PUT',
      url: `/clients/${clientId}/stripeGlobalPayouts/email`,
      body: request,
    });

  /**
   * Update the bank account for a client's Stripe Global Payouts account
   * @param clientId The client ID
   * @param request UpdateBankAccountRequest
   * @returns updated bank account info
   * @throws ApiError
   */
  public static updateBankAccount = (
    clientId: number,
    request: UpdateBankAccountRequest
  ): CancelablePromise<{ success: boolean; bank_account: BankAccount }> =>
    __request(OpenAPI, {
      method: 'PUT',
      url: `/clients/${clientId}/stripeGlobalPayouts/bankAccount`,
      body: request,
    });

  /**
   * Create a recipient and return a Stripe-hosted onboarding link
   * @param clientId The client ID
   * @param email The recipient's email address
   * @returns onboarding URL
   * @throws ApiError
   */
  public static createOnboardingLink = (clientId: number, email: string): CancelablePromise<{ url: string }> =>
    __request(OpenAPI, {
      method: 'POST',
      url: `/clients/${clientId}/stripeGlobalPayouts/onboarding`,
      body: { email },
    });

  /**
   * Create a new Stripe Global Payouts account for a client
   * @param clientId The client ID
   * @param recipientData The recipient data
   * @param bankAccountData Optional bank account data
   * @returns The client model with stripe_global_payouts_account_id
   * @throws ApiError
   */
  public static createRecipient = (
    clientId: number,
    recipientData: CreateRecipientRequest,
    bankAccountData?: CreateBankAccountRequest
  ): CancelablePromise<{ stripe_global_payouts_account_id: string }> =>
    __request(OpenAPI, {
      method: 'POST',
      url: `/clients/${clientId}/stripeGlobalPayouts`,
      body: {
        recipient_data: recipientData,
        bank_account_data: bankAccountData,
      },
    });
}
